import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input } from '@/components/common'
import { ultraadminBillingApi, type BillingPrice } from '@/services/api/billing'
import { ultraadminApi, type CountryInfo, type School } from '@/services/api/ultraadmin'
import { useToast } from '@/components/ui/ToastProvider'
import { extractErrorMessage } from '@/utils/errorHandler'
import { applyDeal, asNumber, formatMoney } from '@/utils/billingDeal'
import { SchoolListFilters } from '@/components/ultraadmin/SchoolListFilters'
import {
  EMPTY_SCHOOL_LIST_FILTER,
  SCHOOL_LANGUAGES,
  schoolListFilterParams,
  type SchoolListFilterValue,
} from '@/components/ultraadmin/schoolListFilter'

export function UltraadminSchoolTermsPage() {
  const { showToast } = useToast()
  const [filters, setFilters] = useState<SchoolListFilterValue>(EMPTY_SCHOOL_LIST_FILTER)
  const [debounced, setDebounced] = useState(filters)
  const [schools, setSchools] = useState<School[]>([])
  const [prices, setPrices] = useState<BillingPrice[]>([])
  const [countries, setCountries] = useState<CountryInfo[]>([])
  const [deals, setDeals] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(filters), 250)
    return () => window.clearTimeout(t)
  }, [filters])

  useEffect(() => {
    ultraadminApi.getCountries().then(setCountries).catch(() => undefined)
    ultraadminBillingApi.prices().then(setPrices).catch(() => undefined)
  }, [])

  useEffect(() => {
    setLoading(true)
    ultraadminApi.listSchools(schoolListFilterParams(debounced))
      .then((rows) => {
        setSchools(rows)
        setDeals(Object.fromEntries(rows.map((s) => [s.id, String(asNumber(s.billing_deal_percent))])))
      })
      .catch((e) => {
        showToast({ message: extractErrorMessage(e, { loadError: 'Schulen konnten nicht geladen werden.' }), type: 'error', duration: 4000 })
      })
      .finally(() => setLoading(false))
  }, [debounced]) // eslint-disable-line react-hooks/exhaustive-deps

  const priceByCcy = Object.fromEntries(prices.map((p) => [p.currency.toUpperCase(), p]))

  const saveDeal = async (school: School) => {
    const percent = Number(deals[school.id])
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      showToast({ message: 'Rabatt muss zwischen 0 und 100 liegen.', type: 'error', duration: 4000 })
      return
    }
    setSavingId(school.id)
    try {
      const res = await ultraadminBillingApi.deal(school.id, percent)
      setSchools((rows) => rows.map((s) => (s.id === school.id ? { ...s, billing_deal_percent: res.deal_percent } : s)))
      setDeals((d) => ({ ...d, [school.id]: String(asNumber(res.deal_percent)) }))
      showToast({ message: 'Rabatt gespeichert. Wirkt ab der nächsten Rechnung.', type: 'success', duration: 2500 })
    } catch (e: unknown) {
      showToast({ message: extractErrorMessage(e, { loadError: 'Rabatt speichern fehlgeschlagen.' }), type: 'error', duration: 5000 })
    } finally {
      setSavingId(null)
    }
  }

  const languageLabel = (code: string | null | undefined) =>
    SCHOOL_LANGUAGES.find((l) => l.code === code)?.label || code || '—'

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-bold text-gray-900">Schul-Konditionen</h1>
      <p className="text-sm text-gray-600 mt-1 mb-6">
        Rabatt pro Schule. Listenpreis und Dollar-Kopplung stehen unter{' '}
        <Link className="text-indigo-700 underline" to="/ultraadmin/prices">Listenpreise</Link>.
      </p>

      <SchoolListFilters value={filters} onChange={setFilters} countries={countries} resultCount={schools.length} />

      {loading ? (
        <div className="text-sm text-gray-500">Laden…</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Schule</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Kanal</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Land</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Sprache</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Währung</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Liste</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Rabatt %</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Effektiv</th>
                <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500">Dollar</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => {
                const ccy = (s.billing_currency || '').toUpperCase()
                const price = priceByCcy[ccy]
                const list = asNumber(price?.unit_price_gross)
                const deal = asNumber(deals[s.id])
                const effective = applyDeal(list, deal, ccy)
                const dirty = asNumber(deals[s.id]) !== asNumber(s.billing_deal_percent)
                return (
                  <tr key={s.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{s.name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.partner_company_id ? 'bg-blue-50 text-blue-800' : 'bg-emerald-50 text-emerald-800'}`}>
                        {s.partner_company_name || 'Direkt'}
                      </span>
                    </td>
                    <td className="px-3 py-2">{s.country}</td>
                    <td className="px-3 py-2">{languageLabel(s.language)}</td>
                    <td className="px-3 py-2">{ccy || '—'}</td>
                    <td className="px-3 py-2">{price ? formatMoney(list, ccy) : '—'}</td>
                    <td className="px-3 py-2 w-28">
                      <Input className="w-20" value={deals[s.id] ?? '0'} onChange={(e) => setDeals((d) => ({ ...d, [s.id]: e.target.value }))} />
                    </td>
                    <td className="px-3 py-2">
                      <b>{price ? formatMoney(effective, ccy) : '—'}</b>
                      {deal >= 100 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Gratis</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{price?.fx_index_enabled ? 'ja' : 'nein'}</td>
                    <td className="px-3 py-2">
                      <Button type="button" size="sm" disabled={!dirty || savingId === s.id} onClick={() => void saveDeal(s)}>
                        Speichern
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {schools.length === 0 && <p className="p-4 text-sm text-gray-500">Keine Schulen für diese Filter.</p>}
        </div>
      )}
    </div>
  )
}
